import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Stack, TextField, MenuItem, Button, Alert, Chip, FormControlLabel, Switch, Divider } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { whatsappSettingsApi, type WhatsAppSettings } from '../../api/whatsapp.api';
import { extractApiError } from '../../api/error';

const PROVIDERS = [
  { value: 'console', label: 'Console (log only)' },
  { value: 'cloud_api', label: 'WhatsApp Cloud API (future)' },
  { value: 'twilio', label: 'Twilio (future)' },
];

export function WhatsAppSettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['whatsapp-settings'], queryFn: () => whatsappSettingsApi.get() });
  const [form, setForm] = useState<Partial<WhatsAppSettings>>({});
  const [apiKey, setApiKey] = useState('');
  const [testTo, setTestTo] = useState('');
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (data) setForm({ provider: data.provider, fromIdentifier: data.fromIdentifier, accountSid: data.accountSid, active: data.active });
  }, [data]);

  const set = <K extends keyof WhatsAppSettings>(k: K, v: WhatsAppSettings[K]) => setForm((f) => ({ ...f, [k]: v }));

  const saveMut = useMutation({
    mutationFn: () => whatsappSettingsApi.update({ ...form, ...(apiKey ? { apiKey } : {}) }),
    onSuccess: () => { setMsg({ kind: 'success', text: 'WhatsApp settings saved.' }); setApiKey(''); qc.invalidateQueries({ queryKey: ['whatsapp-settings'] }); },
    onError: (e) => setMsg({ kind: 'error', text: extractApiError(e) }),
  });
  const testMut = useMutation({
    mutationFn: () => whatsappSettingsApi.test(testTo),
    onSuccess: (r) => setMsg({ kind: 'success', text: `Test message sent via ${r.provider} (${r.status}).` }),
    onError: (e) => setMsg({ kind: 'error', text: extractApiError(e) }),
  });

  const isCloud = form.provider === 'cloud_api';
  const isTwilio = form.provider === 'twilio';

  return (
    <>
      <PageHeader title="WhatsApp Configuration" subtitle="Provider and credentials for outbound WhatsApp notifications." />
      <Card sx={{ maxWidth: 720 }}>
        <CardHeader title="Provider settings" action={data && <Chip size="small" label={`source: ${data.source}`} color={data.source === 'database' ? 'primary' : 'default'} />} />
        <CardContent>
          <Stack spacing={2}>
            {msg && <Alert severity={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Alert>}
            <TextField select label="Provider" value={form.provider ?? 'console'} onChange={(e) => set('provider', e.target.value)} fullWidth>
              {PROVIDERS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
            {(isCloud || isTwilio) && (
              <>
                <Divider textAlign="left">{isTwilio ? 'Twilio' : 'Cloud API'}</Divider>
                <TextField label={isTwilio ? 'From number' : 'Phone number ID'} value={form.fromIdentifier ?? ''} onChange={(e) => set('fromIdentifier', e.target.value)} fullWidth />
                {isTwilio && <TextField label="Account SID" value={form.accountSid ?? ''} onChange={(e) => set('accountSid', e.target.value)} fullWidth />}
                <TextField label={isTwilio ? 'Auth token' : 'Access token'} type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={data?.hasApiKey ? '•••••• (set — leave blank to keep)' : ''} fullWidth />
              </>
            )}
            <FormControlLabel control={<Switch checked={form.active ?? true} onChange={(e) => set('active', e.target.checked)} />} label="Active" />
            <Button variant="contained" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>Save</Button>
            <Divider textAlign="left">Send test message</Divider>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField label="To number" value={testTo} onChange={(e) => setTestTo(e.target.value)} sx={{ flex: 1 }} />
              <Button variant="outlined" onClick={() => testMut.mutate()} disabled={!testTo || testMut.isPending}>Send Test</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
